<template>
  <v-alert
    v-if="visible"
    type="info"
    variant="tonal"
    density="comfortable"
    class="donation-prompt-banner"
  >
    <DonationInviteContent @support="onSupport" @dismiss="onDismiss" />
  </v-alert>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import DonationInviteContent from '@/components/donations/DonationInviteContent.vue'
import { useAuthStore } from '@/stores/auth'
import { useConcentrationModeStore } from '@/stores/concentrationMode'
import { useDonationPromptStore } from '@/stores/donationPrompt'

const HIDDEN_ROUTE_NAMES = ['home', 'login', 'password-recovery', 'confirm-email', 'email-verification']

export default defineComponent({
  name: 'DonationPromptBanner',

  components: {
    DonationInviteContent,
  },

  setup() {
    const authStore = useAuthStore()
    const donationPromptStore = useDonationPromptStore()
    const concentrationModeStore = useConcentrationModeStore()
    const { active: concentrationModeActive } = storeToRefs(concentrationModeStore)
    const { pendingDefaultBanner } = storeToRefs(donationPromptStore)
    const route = useRoute()

    const visible = computed(() => {
      if (concentrationModeActive.value) return false
      if (HIDDEN_ROUTE_NAMES.includes(String(route.name))) return false
      if (authStore.userType === 'non-authenticated') return false
      return pendingDefaultBanner.value
    })

    const evaluateDefaultTrigger = async () => {
      if (authStore.userType === 'non-authenticated') return
      if (concentrationModeActive.value) return
      if (HIDDEN_ROUTE_NAMES.includes(String(route.name))) return
      if (pendingDefaultBanner.value) return

      donationPromptStore.ensureFirstSeen()
      if (!donationPromptStore.canShow('default')) return

      const shown = await donationPromptStore.tryShow('default')
      if (shown) {
        donationPromptStore.pendingDefaultBanner = true
      }
    }

    const onSupport = () => {
      donationPromptStore.dismissDefaultBanner()
    }

    const onDismiss = () => {
      donationPromptStore.dismissDefaultBanner()
    }

    watch(
      () => [authStore.userType, authStore.accessToken, route.name, concentrationModeActive.value],
      () => {
        void evaluateDefaultTrigger()
      },
      { immediate: true },
    )

    return {
      visible,
      onSupport,
      onDismiss,
    }
  },
})
</script>

<style scoped>
.donation-prompt-banner {
  position: relative;
  z-index: 1;
  border-radius: 0;
  background: rgb(var(--v-theme-surface)) !important;
}

.donation-prompt-banner :deep(.v-alert__underlay) {
  opacity: 1;
  background: rgba(var(--v-theme-info), 0.16) !important;
}
</style>
